
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ImpactFundingOverview from "./ImpactFundingOverview";


import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useMemo, useState } from "react";

import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentOrganization } from "@/modules/organization/useCurrentOrganization";

import type {
  ImpactBankDetails,
  ImpactCardData,
} from "../utils/impactCardData";

import { resolveImpactBankDetails } from "../utils/impactCardData";
import { resolveImpactFundingTotals } from "../utils/impactFundingTotals";
import { formatImpactMoney } from "../utils/impactCurrency";

/* -------------------------------------------------------------------------- */
/* Theme                                                                      */
/* -------------------------------------------------------------------------- */

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#2F6B4F",
  sage: "#DCEADF",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#6B7A73",
  ink: "#1B2430",
  border: "rgba(16,44,36,.10)",
  error: "#B42318",
};

/* -------------------------------------------------------------------------- */
/* Preset amounts                                                             */
/* -------------------------------------------------------------------------- */

const PRESET_AMOUNTS = [
  10_000,
  30_000,
  50_000,
  100_000,
] as const;

type ContributionStep = "amount" | "bank";

/* -------------------------------------------------------------------------- */
/* Bank details card                                                          */
/* -------------------------------------------------------------------------- */

export function BankDetailsCard({
  bank,
}: {
  bank: ImpactBankDetails;
}) {
  const [copied, setCopied] = useState(false);

  const rows: Array<[string, string | undefined]> = [
    ["은행명", bank.bankName],
    ["예금주", bank.accountHolder],
    ["계좌번호", bank.accountNumber],
    ["SWIFT 코드", bank.swiftCode],
    ["입금 시 기재 사항", bank.reference],
  ];

  const copyBank = async () => {
    const lines = rows
      .filter(([, value]) => Boolean(value))
      .map(([label, value]) => `${label}: ${value}`);

    if (bank.instructions) {
      lines.push(bank.instructions);
    }

    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.1, sm: 1.75 },
        borderRadius: { xs: "14px", sm: "18px" },
        border: `1px solid ${c.border}`,
        bgcolor: c.paper,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <AccountBalanceOutlinedIcon
            sx={{
              color: c.forest,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
              fontWeight: 800,
              fontSize: { xs: "0.78rem", sm: "0.875rem" },
              color: c.ink,
              minWidth: 0,
            }}
          >
            카드 제작자 입금 계좌
          </Typography>
        </Stack>

        <IconButton
          size="small"
          onClick={() => void copyBank()}
          aria-label="계좌 정보 복사"
          sx={{ flexShrink: 0 }}
        >
          {copied ? (
            <CheckCircleRoundedIcon
              sx={{
                fontSize: 19,
                color: c.green,
              }}
            />
          ) : (
            <ContentCopyRoundedIcon
              sx={{
                fontSize: 18,
                color: c.forest,
              }}
            />
          )}
        </IconButton>
      </Stack>

      <Stack
        spacing={{ xs: 0.55, sm: 1.1 }}
        sx={{ mt: { xs: 1, sm: 1.5 } }}
      >
        {rows.map(([label, value]) =>
          value ? (
            <Stack
              key={label}
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Typography
                sx={{
                  fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                  fontSize: { xs: "0.65rem", sm: "0.78rem" },
                  color: c.muted,
                  flexShrink: 0,
                  maxWidth: "42%",
                }}
              >
                {label}
              </Typography>

              <Typography
                sx={{
                  fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                  fontWeight: 700,
                  fontSize: { xs: "0.72rem", sm: "0.82rem" },
                  color: c.ink,
                  minWidth: 0,
                  textAlign: {
                    xs: "left",
                    sm: "right",
                  },
                  overflowWrap: "anywhere",
                }}
              >
                {value}
              </Typography>
            </Stack>
          ) : null
        )}
      </Stack>

      {bank.instructions && (
        <Typography
          sx={{
            mt: { xs: 0.85, sm: 1.5 },
            fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
            fontSize: { xs: "0.65rem", sm: "0.78rem" },
            color: c.muted,
            lineHeight: { xs: 1.45, sm: 1.6 },
          }}
        >
          {bank.instructions}
        </Typography>
      )}

      {copied && (
        <Typography
          sx={{
            mt: 1,
            fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
            fontSize: "0.72rem",
            fontWeight: 700,
            color: c.green,
          }}
        >
          계좌 정보가 복사되었습니다.
        </Typography>
      )}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* Contribution page props                                                    */
/* -------------------------------------------------------------------------- */

type ImpactContributePageProps = {
  data: ImpactCardData;
  title: string;
  creatorName?: string;
  isLoggedIn: boolean;
  isOwner: boolean;
  isCollected?: boolean;
  viewerUserId?: number;
  onSignIn?: () => void;
  onSubmit?: (
    amount: number,
    message: string
  ) => Promise<void>;
  submitting?: boolean;
  onOpenStatus?: () => void;
};

/* -------------------------------------------------------------------------- */
/* Main contribution page                                                     */
/* -------------------------------------------------------------------------- */

export default function ImpactContributePage({
  data,
  title,
  creatorName,
  isLoggedIn,
  isOwner,
  onSignIn,
  onSubmit,
  submitting = false,
  onOpenStatus,
}: ImpactContributePageProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { userInfo } = useAuth();

  const {
    organization,
    isOrganizationWorkspace,
  } = useCurrentOrganization();

  const identity =
    isOrganizationWorkspace && organization?.name
      ? {
          type: "단체" as const,
          name: organization.name,
        }
      : {
          type: "개인" as const,
          name:
            userInfo?.username ||
            userInfo?.first_name ||
            "본인",
        };

  const bank = resolveImpactBankDetails(
    data.bankDetails,
    creatorName
  );

  /* ---------------------------------------------------------------------- */
  /* Contribution state                                                     */
  /* ---------------------------------------------------------------------- */

  const [step, setStep] =
    useState<ContributionStep>("amount");

  const [selectedPreset, setSelectedPreset] =
    useState<number | null>(null);

  const [customAmount, setCustomAmount] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [submitError, setSubmitError] =
    useState("");

  const [localSubmitting, setLocalSubmitting] =
    useState(false);

  const amount =
    selectedPreset !== null
      ? selectedPreset
      : customAmount.trim()
        ? Number(customAmount)
        : 0;

  const isValidAmount =
    Number.isSafeInteger(amount) &&
    amount > 0;

  const isBusy =
    submitting || localSubmitting;

  /* ---------------------------------------------------------------------- */
  /* Amount handlers                                                        */
  /* ---------------------------------------------------------------------- */

  const handlePresetClick = (value: number) => {
    if (isBusy) return;

    setSelectedPreset(value);

    // Never populate the custom input from a preset.
    setCustomAmount("");

    setSubmitError("");
  };

  const handleCustomAmountChange = (
    value: string
  ) => {
    if (isBusy) return;

    const digitsOnly = value.replace(/\D/g, "");

    setCustomAmount(digitsOnly);

    // A custom amount replaces any preset selection.
    setSelectedPreset(null);

    setSubmitError("");
  };

  const handleContinue = () => {
    if (!isValidAmount || isBusy) return;

    setSubmitError("");
    setStep("bank");
  };

  const handleBack = () => {
    if (isBusy) return;

    setSubmitError("");
    setStep("amount");
  };

  /* ---------------------------------------------------------------------- */
  /* Submission handler                                                     */
  /* ---------------------------------------------------------------------- */

  const handleSubmit = async () => {
    if (!isValidAmount || isBusy) return;

    setSubmitError("");

    if (!isLoggedIn) {
      onSignIn?.();
      return;
    }

    if (!onSubmit) {
      setSubmitError(
        "현재 기부 참여 신청을 할 수 없습니다."
      );
      return;
    }

    try {
      setLocalSubmitting(true);

      await onSubmit(
        amount,
        message.trim()
      );

      // Do not mark the transfer as verified here.
      // Verification is handled separately by the creator.
      setMessage("");
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "기부 참여 신청을 제출하지 못했습니다. 다시 시도해 주세요."
      );
    } finally {
      setLocalSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <Stack
      spacing={{ xs: 1.05, sm: 2 }}
      sx={{
        width: "100%",
        minWidth: 0,
        pb: { xs: 0.5, sm: 2 },
      }}
    >
      {/* ================================================================ */}
      {/* STEP 1 — SELECT AMOUNT                                           */}
      {/* ================================================================ */}

      {step === "amount" && (
        <>
          <Box>
            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontWeight: 800,
                fontSize: {
                  xs: "1.28rem",
                  sm: "1.8rem",
                },
                lineHeight: 1.2,
                letterSpacing: "-0.03em",
                color: c.ink,
              }}
            >
              기부 참여하기
            </Typography>

            <Typography
              sx={{
                mt: { xs: 0.35, sm: 0.65 },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.72rem", sm: "0.88rem" },
                lineHeight: { xs: 1.45, sm: 1.65 },
                color: c.muted,
              }}
            >
              네팔 긴급 구호·복구를 위한 기부 금액을 선택해 주세요.
            </Typography>
          </Box>


          {/* Preset amounts */}

          <Box>
            <Typography
              sx={{
                mb: { xs: 0.55, sm: 1.2 },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.74rem", sm: "0.85rem" },
                fontWeight: 800,
                color: c.ink,
              }}
            >
              금액 선택
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(2, minmax(0, 1fr))",
                gap: { xs: 0.55, sm: 1.1 },
              }}
            >
              {PRESET_AMOUNTS.map((value) => {
                const isSelected =
                  selectedPreset === value;

                return (
                  <Button
                    key={value}
                    type="button"
                    variant="outlined"
                    disabled={isBusy}
                    aria-pressed={isSelected}
                    onClick={() =>
                      handlePresetClick(value)
                    }
                    sx={{
                      minHeight: { xs: 40, sm: 58 },
                      py: { xs: 0.35, sm: 1 },
                      borderRadius: { xs: "12px", sm: "14px" },
                      borderWidth: isSelected ? 2 : 1,
                      borderColor: isSelected
                        ? c.green
                        : c.border,
                      bgcolor: isSelected
                        ? c.mist
                        : c.paper,
                      color: c.forestDeep,
                      fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                      fontSize: { xs: "0.78rem", sm: "0.98rem" },
                      fontWeight: 800,
                      textTransform: "none",

                      "&:hover": {
                        borderWidth: isSelected
                          ? 2
                          : 1,
                        borderColor: c.green,
                        bgcolor: c.mist,
                      },
                    }}
                  >
                    ₩{value.toLocaleString("ko-KR")}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* Custom amount */}

          <Box>
            <Typography
              sx={{
                mb: { xs: 0.45, sm: 1 },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.74rem", sm: "0.85rem" },
                fontWeight: 800,
                color: c.ink,
              }}
            >
              직접 금액 입력
            </Typography>

            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              type="text"
              value={customAmount}
              disabled={isBusy}
              onChange={(event) =>
                handleCustomAmountChange(
                  event.target.value
                )
              }
              placeholder="기부 금액을 입력해 주세요"
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  "aria-label":
                    "원화 기부 금액",
                },
                input: {
                  startAdornment: (
                    <Typography
                      sx={{
                        mr: 1,
                        fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                        fontWeight: 800,
                        color: c.forestDeep,
                      }}
                    >
                      ₩
                    </Typography>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: c.paper,
                },
              }}
            />

            <Typography
              sx={{
                mt: 0.5,
                display: { xs: "none", sm: "block" },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: "0.73rem",
                lineHeight: 1.5,
                color: c.muted,
              }}
            >
              위의 추천 금액을 선택했다면 이 입력란은 비워 두세요.
            </Typography>
          </Box>

          {/* Amount summary */}

          {isValidAmount && (
            <Box
              sx={{
                p: { xs: 1, sm: 1.75 },
                borderRadius: { xs: "12px", sm: "16px" },
                bgcolor: c.mist,
              }}
            >
              <Typography
                sx={{
                  fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  color: c.muted,
                }}
              >
                신청 금액
              </Typography>

              <Typography
                sx={{
                  mt: 0.2,
                  fontFamily: `${HOPE_SERIF}, "Noto Serif KR", serif`,
                  fontWeight: 700,
                  fontSize: { xs: "1.15rem", sm: "1.7rem" },
                  color: c.forestDeep,
                }}
              >
                {formatImpactMoney(amount)}
              </Typography>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={!isValidAmount || isBusy}
            onClick={handleContinue}
            endIcon={
              <ArrowForwardIosRoundedIcon
                sx={{ fontSize: { xs: 13, sm: 15 } }}
              />
            }
            sx={{
              minHeight: { xs: 42, sm: 52 },
              fontSize: { xs: "0.82rem", sm: "0.9375rem" },
              borderRadius: "14px",
              bgcolor: c.forest,
              fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
              fontWeight: 800,
              textTransform: "none",

              "&:hover": {
                bgcolor: c.forestDeep,
              },
            }}
          >
            입금 계좌 확인하기
          </Button>
        </>
      )}

      {/* ================================================================ */}
      {/* STEP 2 — BANK DETAILS                                            */}
      {/* ================================================================ */}

      {step === "bank" && (
        <>
          <Button
            onClick={handleBack}
            disabled={isBusy}
            startIcon={
              <ArrowBackRoundedIcon
                sx={{ fontSize: { xs: 18, sm: 24 } }}
              />
            }
            sx={{
              alignSelf: "flex-start",
              px: 0,
              minHeight: { xs: 32, sm: 36 },
              py: 0,
              fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
              fontWeight: 700,
              fontSize: { xs: "0.72rem", sm: "0.8rem" },
              color: c.forest,
              textTransform: "none",
            }}
          >
            금액 변경
          </Button>

          <Box>
            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontWeight: 800,
                fontSize: {
                  xs: "1.22rem",
                  sm: "1.75rem",
                },
                letterSpacing: "-0.03em",
                color: c.ink,
              }}
            >
              계좌이체 안내
            </Typography>

            <Typography
              sx={{
                mt: { xs: 0.3, sm: 0.65 },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.72rem", sm: "0.86rem" },
                lineHeight: { xs: 1.45, sm: 1.65 },
                color: c.muted,
              }}
            >
              신청 금액과 입금 계좌를 확인해 주세요.
            </Typography>
          </Box>

          {/* Selected amount */}

          <Box
            sx={{
              p: { xs: 0.9, sm: 1.75 },
              borderRadius: { xs: "12px", sm: "16px" },
              bgcolor: c.mist,
            }}
          >
            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.65rem", sm: "0.74rem" },
                color: c.muted,
              }}
            >
              선택한 기부 금액
            </Typography>

            <Typography
              sx={{
                mt: 0.15,
                fontFamily: `${HOPE_SERIF}, "Noto Serif KR", serif`,
                fontWeight: 700,
                fontSize: { xs: "1.2rem", sm: "1.8rem" },
                color: c.forestDeep,
              }}
            >
              {formatImpactMoney(amount)}
            </Typography>
          </Box>

          {/* Bank account */}

          <BankDetailsCard bank={bank} />

          {/* Transfer explanation */}

          <Box
            sx={{
              p: { xs: 0.85, sm: 1.5 },
              borderRadius: { xs: "12px", sm: "14px" },
              bgcolor: c.mist,
            }}
          >
            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontWeight: 800,
                fontSize: { xs: "0.72rem", sm: "0.8rem" },
                color: c.forestDeep,
              }}
            >
              기부 참여 절차
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                display: { xs: "block", sm: "none" },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: "0.65rem",
                lineHeight: 1.45,
                color: c.muted,
              }}
            >
              신청 후 아래 계좌로 입금하세요. 제작자 확인 후 금액이 인증됩니다.
            </Typography>

            <Typography
              sx={{
                mt: 0.65,
                display: { xs: "none", sm: "block" },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: "0.78rem",
                lineHeight: 1.7,
                color: c.muted,
              }}
            >
              선택한 금액으로 기부 참여를 신청한 뒤 카드 제작자의 계좌로 입금해 주세요. 카드 제작자가 입금 내역을 확인한 후 기부 금액을 인증합니다. 모금액은 네팔 총리 구호기금과 아트 오브 리빙 네팔 지원을 위해 사용될 예정입니다.
            </Typography>
          </Box>

          {/* Optional message */}

          <Box>
            <Typography
              sx={{
                mb: { xs: 0.4, sm: 0.85 },
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.72rem", sm: "0.82rem" },
                fontWeight: 800,
                color: c.ink,
              }}
            >
              응원 메시지 (선택)
            </Typography>

            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              multiline
              minRows={isMobile ? 1 : 2}
              maxRows={isMobile ? 2 : 4}
              value={message}
              disabled={isBusy}
              onChange={(event) =>
                setMessage(event.target.value)
              }
              placeholder="응원의 메시지를 남겨 주세요..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: c.paper,
                },
              }}
            />
          </Box>

          {/* Submission error */}

          {submitError && (
            <Typography
              role="alert"
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: "0.8rem",
                color: c.error,
              }}
            >
              {submitError}
            </Typography>
          )}

          {/* Final commitment action */}

          <Button
            fullWidth
            variant="contained"
            disabled={
              !isValidAmount ||
              isBusy ||
              (isLoggedIn && !onSubmit)
            }
            onClick={() => void handleSubmit()}
            startIcon={
              isBusy ? (
                <CircularProgress
                  size={17}
                  color="inherit"
                />
              ) : (
                <CheckCircleRoundedIcon />
              )
            }
            sx={{
              minHeight: { xs: 42, sm: 52 },
              fontSize: { xs: "0.82rem", sm: "0.9375rem" },
              borderRadius: "14px",
              bgcolor: c.forest,
              fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
              fontWeight: 800,
              textTransform: "none",

              "&:hover": {
                bgcolor: c.forestDeep,
              },
            }}
          >
            {isBusy
              ? "제출 중..."
              : isLoggedIn
                ? "기부 참여 신청하기"
                : "로그인 후 기부 참여하기"}
          </Button>

          <Typography
            sx={{
              fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
              fontSize: { xs: "0.62rem", sm: "0.72rem" },
              lineHeight: { xs: 1.4, sm: 1.6 },
              textAlign: "center",
              color: c.muted,
            }}
          >
            입금 확인 전까지 기부 참여 상태는 확인 대기로 표시됩니다.
          </Typography>
        </>
      )}

      {/* ================================================================ */}
      {/* CONTRIBUTION STATUS NAVIGATION                                   */}
      {/* ================================================================ */}

      {isOwner && (
        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
            fontSize: "0.8rem",
            lineHeight: 1.5,
            color: c.muted,
          }}
        >
          이 화면은 기부 참여자용입니다. 입금 내역은 기부 참여 현황 페이지에서 확인할 수 있습니다.
        </Typography>
      )}

      {onOpenStatus && !(isMobile && step === "bank") && (
        <Box
          component="button"
          type="button"
          onClick={onOpenStatus}
          sx={{
            width: "100%",
            textAlign: "left",
            border: `1px solid ${c.border}`,
            borderRadius: { xs: "12px", sm: "16px" },
            bgcolor: c.paper,
            px: { xs: 1.1, sm: 1.5 },
            py: { xs: 0.85, sm: 1.35 },
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1.15 },
          }}
        >
          <VerifiedOutlinedIcon
            sx={{
              color: c.forest,
              fontSize: { xs: 20, sm: 24 },
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontWeight: 800,
                fontSize: { xs: "0.78rem", sm: "1rem" },
                color: c.ink,
              }}
            >
              기부 참여 현황 보기
            </Typography>

            <Typography
              sx={{
                fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
                fontSize: { xs: "0.65rem", sm: "0.78rem" },
                color: c.muted,
              }}
            >
              신청·인증 금액 확인
            </Typography>
          </Box>

          <ArrowForwardIosRoundedIcon
            sx={{
              fontSize: 14,
              color: c.muted,
            }}
          />
        </Box>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/* Contribution status page                                                   */
/* -------------------------------------------------------------------------- */


export function ImpactContributionStatusPage({
  data,
  isLoggedIn,
  isOwner,
  viewerUserId,
}: {
  data: ImpactCardData;
  isLoggedIn: boolean;
  isOwner: boolean;
  viewerUserId?: number;
}) {
  const allContributors = data.contributors ?? [];

  const campaignFunding = useMemo(
    () => resolveImpactFundingTotals(data),
    [data]
  );

  const {
    goalAmount,
    committedAmount: totalCommitted,
    verifiedAmount: totalVerified,
    contributorCount,
  } = campaignFunding;

  /* -------------------------------------------------------------- */
  /* Current viewer's contributions                                 */
  /* -------------------------------------------------------------- */

  const mine = useMemo(() => {
    if (isOwner) {
      return allContributors;
    }

    if (!isLoggedIn || !viewerUserId) {
      return [];
    }

    return allContributors.filter(
      (item) =>
        Number(item.userId) === Number(viewerUserId)
    );
  }, [
    allContributors,
    isOwner,
    isLoggedIn,
    viewerUserId,
  ]);

  const ownCommitted = mine.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  const ownVerified = mine
    .filter((item) => item.status === "Verified")
    .reduce(
      (sum, item) =>
        sum + (item.verifiedAmount ?? item.amount),
      0
    );

  const statusLabel = (status: string) => {
    if (status === "Verified") {
      return "인증 완료";
    }

    if (status === "Rejected") {
      return "반려";
    }

    if (status === "Pending") {
      return "확인 대기";
    }

    return "참여 신청 완료";
  };

  const statusColor = (status: string) => {
    if (status === "Verified") {
      return c.green;
    }

    if (status === "Rejected") {
      return c.error;
    }

    return c.muted;
  };

  return (
    <Stack
      spacing={{ xs: 1.25, sm: 2.5 }}
      sx={{
        width: "100%",
        minWidth: 0,
        pb: { xs: 1, sm: 3 },
      }}
    >
      {/* ============================================================ */}
      {/* OVERALL CAMPAIGN FUNDING STATUS                              */}
      {/* ============================================================ */}

      <ImpactFundingOverview
        goalAmount={goalAmount}
        committedAmount={totalCommitted}
        verifiedAmount={totalVerified}
        contributorCount={contributorCount}
      />

      {isOwner ? (
        <>
      {/* ============================================================ */}
      {/* CREATOR: ALL CONTRIBUTIONS                                   */}
      {/* ============================================================ */}

      <Box>
        <Typography
          component="h2"
          sx={{
            fontFamily: `${HOPE_SERIF}, "Noto Serif KR", serif`,
            fontWeight: 700,
            fontSize: "1.2rem",
            color: c.forestDeep,
            mb: 0.5,
          }}
        >
          전체 기부 참여 내역
        </Typography>

        <Typography
          sx={{
            fontFamily: `${HOPE_SANS}, "Noto Sans KR", sans-serif`,
            fontSize: "0.78rem",
            lineHeight: 1.6,
            color: c.muted,
          }}
        >
          참여 신청과 입금 인증 상태를 확인하세요.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.1}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: 1.5,
            borderRadius: "16px",
            bgcolor: c.mist,
          }}
        >
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: "0.68rem",
              color: c.muted,
            }}
          >
            신청 금액
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              fontSize: {
                xs: "1.02rem",
                sm: "1.2rem",
              },
              color: c.forestDeep,
              overflowWrap: "anywhere",
            }}
          >
            {formatImpactMoney(ownCommitted)}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: 1.5,
            borderRadius: "16px",
            bgcolor: c.sage,
          }}
        >
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: "0.68rem",
              color: c.muted,
            }}
          >
            인증 금액
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              fontSize: {
                xs: "1.02rem",
                sm: "1.2rem",
              },
              color: c.forestDeep,
              overflowWrap: "anywhere",
            }}
          >
            {formatImpactMoney(ownVerified)}
          </Typography>
        </Box>
      </Stack>

      {/* ============================================================ */}
      {/* CONTRIBUTION HISTORY                                         */}
      {/* ============================================================ */}

      <Stack spacing={1.1}>
        {mine.length === 0 ? (
          <Box
            sx={{
              p: 2,
              borderRadius: "16px",
              bgcolor: c.mist,
            }}
          >
            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: "0.86rem",
                lineHeight: 1.6,
                color: c.muted,
              }}
            >
              {isLoggedIn
                ? "이 카드에 신청한 기부 내역이 아직 없습니다."
                : "기부 참여 내역을 확인하려면 로그인해 주세요."}
            </Typography>
          </Box>
        ) : (
          mine.map((item) => (
            <Box
              key={String(item.id)}
              sx={{
                p: 1.5,
                borderRadius: "16px",
                bgcolor: c.paper,
                border: `1px solid ${c.border}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: HOPE_SANS,
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      color: c.forestDeep,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.name}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.35,
                      fontFamily: HOPE_SANS,
                      fontSize: "0.73rem",
                      color: c.muted,
                    }}
                  >
                    {item.accountType || "개인"}
                    {" · "}
                    {new Date(
                      item.contributedAt
                    ).toLocaleDateString("ko-KR")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: HOPE_SERIF,
                      fontWeight: 700,
                      fontSize: "0.98rem",
                      color: c.forestDeep,
                    }}
                  >
                    {formatImpactMoney(item.amount)}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.3,
                      fontFamily: HOPE_SANS,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: statusColor(item.status),
                    }}
                  >
                    {statusLabel(item.status)}
                  </Typography>
                </Box>
              </Stack>

              {item.status === "Verified" &&
              item.verifiedAmount != null &&
              item.verifiedAmount !== item.amount ? (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: HOPE_SANS,
                    fontSize: "0.73rem",
                    color: c.green,
                  }}
                >
                  실제 인증 금액:{" "}
                  {formatImpactMoney(
                    item.verifiedAmount
                  )}
                </Typography>
              ) : null}
            </Box>
          ))
        )}
      </Stack>
        </>
      ) : null}
    </Stack>
  );
}